import React from 'react';
import { motion } from "motion/react";

interface EventScheduleCardSkeletonProps {
  count?: number;
}

const SkeletonPulse: React.FC<{ className?: string; delay?: number }> = ({ 
  className = "", 
  delay = 0 
}) => (
  <motion.div
    className={`bg-gray-700 rounded ${className}`}
    animate={{
      opacity: [0.4, 0.8, 0.4],
    }}
    transition={{
      duration: 1.5,
      repeat: Infinity,
      delay,
      ease: "easeInOut"
    }}
  />
);

const EventScheduleCardSkeletonItem: React.FC = () => (
  <motion.div
    className="bg-gray-900 border border-gray-800 rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg"
    initial={{ opacity: 0, y: 20, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    whileHover={{ 
      y: -4,
      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
      transition: { type: "spring", stiffness: 300, damping: 30 }
    }}
    transition={{
      type: "spring",
      stiffness: 300,
      damping: 30,
      mass: 0.8
    }}
  >
    {/* Header skeleton */}
    <div className="relative h-32 sm:h-48 lg:h-64 bg-gray-800">
      <div className="absolute inset-0 bg-gray-800/60"></div>
      
      <div className="relative z-10 h-full flex flex-col justify-between p-3 sm:p-6 lg:p-8">
        {/* Event info skeleton */}
        <div className="space-y-2">
          <SkeletonPulse className="h-4 sm:h-5 lg:h-6 w-3/4" delay={0.1} />
          <SkeletonPulse className="h-3 sm:h-4 w-full hidden sm:block" delay={0.2} />
          <SkeletonPulse className="h-3 sm:h-4 w-2/3 hidden sm:block" delay={0.3} />
        </div>
        
        {/* Schedule info skeleton */}
        <div className="space-y-1 sm:space-y-2">
          <div className="flex items-center gap-1 sm:gap-2">
            <SkeletonPulse className="h-3 w-3 sm:h-4 sm:w-4 rounded-full" delay={0.4} />
            <SkeletonPulse className="h-3 sm:h-4 w-32" delay={0.5} />
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <SkeletonPulse className="h-3 w-3 sm:h-4 sm:w-4 rounded-full" delay={0.6} />
            <SkeletonPulse className="h-3 sm:h-4 w-20" delay={0.7} />
            <SkeletonPulse className="h-3 sm:h-4 w-2 hidden sm:block" delay={0.8} />
            <SkeletonPulse className="h-3 sm:h-4 w-8 hidden sm:block" delay={0.9} />
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <SkeletonPulse className="h-3 w-3 sm:h-4 sm:w-4 rounded-full" delay={1.0} />
            <SkeletonPulse className="h-3 sm:h-4 w-16" delay={1.1} />
          </div>
        </div>
        
        {/* Expand button skeleton */}
        <div className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4">
          <SkeletonPulse className="h-8 w-8 sm:h-10 sm:w-10 rounded-full" delay={1.2} />
        </div>
      </div>
    </div>
  </motion.div>
);

export const EventScheduleCardSkeleton: React.FC<EventScheduleCardSkeletonProps> = ({ count = 3 }) => (
  <div className="space-y-3 sm:space-y-4 lg:space-y-6 pb-2 sm:pb-3 lg:pb-4">
    {Array.from({ length: count }).map((_, index) => (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ 
          delay: 0.1 + index * 0.1, 
          type: "spring", 
          stiffness: 300, 
          damping: 30,
          mass: 0.8
        }}
      >
        <EventScheduleCardSkeletonItem />
      </motion.div>
    ))}
  </div>
);
